import * as React from 'react';

import { type VariantProps, cva } from 'class-variance-authority';

import { cn } from '../lib/utils';

/**
 * 🔴 KEIN `translate-y-[-3px]` AM TEXT — entfernt am 21.09.2026, vom
 * Auftraggeber am Bild gemeldet („text und das symbol nicht auf einer linie").
 *
 * Hier stand `[&>svg+div]:translate-y-[-3px]`, uebernommen aus der
 * shadcn-Vorlage. Der Selektor trifft ein `div` DIREKT nach dem Symbol — und
 * das ist genau der Fall OHNE Titel:
 *
 *     mit Titel      svg + h5   ⇒ der Selektor greift gar nicht
 *     ohne Titel     svg + div  ⇒ er greift, und schiebt den Text hoch
 *
 * Gemessen wird der Kasten damit an der einen Stelle falsch, an der er
 * ueberhaupt etwas tut. Nachgerechnet:
 *
 *     Polster `py-3.5` = 14 · Symbol absolut `top-4` = 16, Groesse 16
 *
 *     Symbol   16 .. 32   ⇒ Mitte 24
 *     Text     14 .. 34   (Zeilenhoehe 20 bei `text-sm`) ⇒ Mitte 24
 *
 * OHNE Versatz liegen beide Mitten auf 24 — exakt auf einer Linie. MIT
 * Versatz steht der Text auf 21, also 3 px hoeher. Genau das war im
 * Bildschirmfoto zu sehen.
 *
 * ⚠️ REICHWEITE: 104 Dateien nutzen `<Alert>` ohne Titel, 43 mit. Die 43
 * aendern sich NICHT — bei ihnen folgt auf das Symbol ein `h5`, und der
 * Selektor hat dort nie gegriffen.
 */
/**
 * Die eine Meldungsform (#1329, T19) — subcontracts: getönte Fläche, kein
 * Rahmen, 8 px Radius. Der Ton sitzt in der Fläche (`*-subtle`) und im
 * Zeichen, der Text bleibt lesbar (`*-on-subtle`). Inline-Fehler, Hinweise und
 * die Fehlerarten aus `@kit/ui/zustaende` rendern alle hierüber.
 */
const alertVariants = cva(
  '[&>svg]:text-foreground rounded-kachel relative flex w-full flex-col gap-y-2 px-4 py-3.5 text-sm [&>svg]:absolute [&>svg]:top-4 [&>svg]:left-4 [&>svg~*]:pl-7',
  {
    variants: {
      variant: {
        default: 'bg-tile text-foreground',
        destructive:
          'bg-danger-subtle text-danger-on-subtle [&>svg]:text-danger',
        success:
          'bg-success-subtle text-success-on-subtle [&>svg]:text-success-text',
        warning:
          'bg-warning-subtle text-warning-on-subtle [&>svg]:text-warning-text',
        info: 'bg-info-subtle text-info-on-subtle [&>svg]:text-info',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const Alert: React.FC<
  React.ComponentPropsWithRef<'div'> & VariantProps<typeof alertVariants>
> = ({ className, variant, ...props }) => (
  <div
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
);
Alert.displayName = 'Alert';

const AlertTitle: React.FC<React.ComponentPropsWithRef<'h5'>> = ({
  className,
  ...props
}) => (
  <h5
    className={cn('leading-none font-bold tracking-tight', className)}
    {...props}
  />
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription: React.FC<React.ComponentPropsWithRef<'div'>> = ({
  className,
  ...props
}) => (
  <div
    className={cn('text-sm font-normal [&_p]:leading-relaxed', className)}
    {...props}
  />
);
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
